import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import axios from 'axios'
import { authApi, integrationsApi, getErrorMessage, type User, type LoginCredentials, type RegisterData, type RegisterResponse, type PlanStatus } from '@/lib/api'
import { setTokens, clearTokens, getAccessToken, authStateStorage } from '@/lib/tokenStorage'
import { captureEvent } from '@/lib/posthog'
import { toast } from 'sonner'

// Kick off a silent, background re-test of the user's configured integrations so
// the Settings status dots are fresh after login. Fire-and-forget: never blocks
// or surfaces errors, and runs only on an explicit login (not on token refresh or
// app boot from a saved session).
function refreshIntegrationStatuses() {
  integrationsApi.refreshStatuses().catch(() => {})
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  // Resolved permission levels for the current user's role, e.g.
  // {"workflows.activate": "gated"} -- fetched once at login/boot, read by
  // usePermission(). UX-only; require_permission server-side is the real
  // security boundary regardless of what this map says.
  permissions: Record<string, string>
  // Whether the account is on the free trial and its numeric caps, e.g.
  // {plan_name: "trial", is_trial: true, limits: {contacts: 50, ...}} --
  // fetched once at login/boot alongside `permissions`, read by
  // useTrialStatus(). Every role gets this (unlike billingStore.subscription,
  // gated to billing.manage_subscription).
  planStatus: PlanStatus | null
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<RegisterResponse>
  verifyOtp: (email: string, code: string) => Promise<void>
  resendOtp: (email: string) => Promise<void>
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
  fetchPermissions: () => Promise<void>
  fetchPlanStatus: () => Promise<void>
  updateUser: (user: User) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      permissions: {},
      planStatus: null,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true })
        try {
          const response = await authApi.login(credentials)
          const { access_token, refresh_token, user } = response.data

          // Store tokens — localStorage or sessionStorage depending on the
          // Remember-me flag the login form set before this call.
          setTokens(access_token, refresh_token)

          // permissions reset in the SAME set(): a new session must never
          // inherit the previous identity's map. Accept-invite is the real
          // case — the invitee was just the owner of their own solo account,
          // and for the beat before their (narrower) map arrived, the stale
          // owner map let the sidebar mount billing UI whose fetch then 403'd
          // ("Failed to load subscription"). Empty degrades to not-allowed.
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            permissions: {},
            planStatus: null
          })

          captureEvent('user_logged_in', { method: 'password' })
          refreshIntegrationStatuses()
          get().fetchPermissions()
          get().fetchPlanStatus()
          toast.success('Welcome back!')
        } catch (error: unknown) {
          const isUnverified =
            axios.isAxiosError(error) && error.response?.data?.detail === 'EMAIL_NOT_VERIFIED'
          toast.error(
            isUnverified ? 'Please verify your email to continue.' : getErrorMessage(error, 'Login failed')
          )
          set({ isLoading: false })
          throw error
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true })
        try {
          const response = await authApi.register(data)
          set({ isLoading: false })

          // Funnel entry point: signup → verify email → create workflow → launch campaign.
          captureEvent('user_signed_up', { method: 'password' })
          toast.success(response.data.message)
          return response.data
        } catch (error: unknown) {
          toast.error(getErrorMessage(error, 'Registration failed'))
          set({ isLoading: false })
          throw error
        }
      },

      verifyOtp: async (email: string, code: string) => {
        set({ isLoading: true })
        try {
          const response = await authApi.verifyOtp({ email, code })
          const { access_token, refresh_token, user } = response.data

          // Store tokens
          setTokens(access_token, refresh_token)

          // See login(): a new session never inherits the old permission map.
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            permissions: {},
            planStatus: null
          })

          captureEvent('email_verified', { method: 'otp' })
          refreshIntegrationStatuses()
          get().fetchPermissions()
          get().fetchPlanStatus()
          toast.success('Email verified! Welcome aboard.')
        } catch (error: unknown) {
          toast.error(getErrorMessage(error, 'Verification failed'))
          set({ isLoading: false })
          throw error
        }
      },

      resendOtp: async (email: string) => {
        try {
          await authApi.resendOtp({ email })
          toast.success('A new code has been sent to your email.')
        } catch (error: unknown) {
          toast.error(getErrorMessage(error, 'Could not resend the code'))
          throw error
        }
      },

      // Establish a session from tokens minted by the backend SSO callback
      // (social / enterprise SSO). The backend already resolved/created the
      // local user and issued the SAME app JWTs as password login, so we just
      // persist the tokens and load the profile via the unchanged /me flow.
      loginWithTokens: async (accessToken: string, refreshToken: string) => {
        set({ isLoading: true })
        setTokens(accessToken, refreshToken)
        try {
          const response = await authApi.getMe()
          // See login(): a new session never inherits the old permission map.
          set({ user: response.data, isAuthenticated: true, isLoading: false, permissions: {}, planStatus: null })
          captureEvent('user_logged_in', { method: 'sso' })
          refreshIntegrationStatuses()
          get().fetchPermissions()
          get().fetchPlanStatus()
          toast.success('Welcome back!')
        } catch (error: unknown) {
          // Token handoff failed — clear and surface to the caller.
          set({ user: null, isAuthenticated: false, isLoading: false })
          clearTokens()
          toast.error(getErrorMessage(error, 'Sign-in failed'))
          throw error
        }
      },

      logout: () => {
        // Fire logout API FIRST so the request interceptor can pick up the access
        // token from localStorage and send an authenticated DELETE. Clearing the
        // tokens before this call made the request go out unauthenticated and
        // return 403 from get_current_user. The promise is fire-and-forget — we
        // don't block UI on it.
        authApi.logout().catch(() => {})

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          permissions: {},
          planStatus: null
        })

        // Now clear tokens. clearTokens() also drops the Zustand-persisted
        // 'auth-storage' snapshot (from both localStorage and sessionStorage),
        // so it must run AFTER set() — otherwise the persist middleware would
        // immediately rewrite the key with null state.
        clearTokens()

        toast.success('Logged out successfully')
      },

      refreshUser: async () => {
        const token = getAccessToken()
        if (!token) {
          // No token at all — clear any stale persisted state
          set({ user: null, isAuthenticated: false })
          return
        }

        try {
          const response = await authApi.getMe()
          set({ user: response.data, isAuthenticated: true })
          get().fetchPermissions()
          get().fetchPlanStatus()
        } catch (error: unknown) {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            // 401 means both access + refresh tokens are expired.
            // The Axios interceptor already tried to refresh and failed,
            // clearing tokens and redirecting to /login. Just clean up state.
            set({ user: null, isAuthenticated: false })
          } else {
            // Network error or server error — keep existing session data
            // so the user isn't logged out due to a transient failure.
            console.warn('Failed to refresh user data:', getErrorMessage(error))
          }
        }
      },

      // Best-effort: never blocks or surfaces errors, same as
      // refreshIntegrationStatuses(). A failure just means usePermission()
      // sees an empty map (every check reads as not-allowed) until the next
      // successful fetch -- the backend's own require_permission on each
      // route is unaffected either way.
      fetchPermissions: async () => {
        try {
          const response = await authApi.getPermissions()
          set({ permissions: response.data })
        } catch (error: unknown) {
          console.warn('Failed to fetch permission map:', getErrorMessage(error))
        }
      },

      // Best-effort, same posture as fetchPermissions(): a failure just
      // means useTrialStatus() sees no plan info until the next successful
      // fetch (trial-aware UI simply doesn't render), never a thrown error.
      fetchPlanStatus: async () => {
        try {
          const response = await authApi.getPlanStatus()
          set({ planStatus: response.data })
        } catch (error: unknown) {
          console.warn('Failed to fetch plan status:', getErrorMessage(error))
        }
      },

      updateUser: (user: User) => {
        set({ user })
      }
    }),
    {
      name: 'auth-storage',
      // Follow the tokens: when the user didn't tick "Remember me" this
      // snapshot goes to sessionStorage too. Persisting `isAuthenticated: true`
      // to localStorage while the token lived in sessionStorage would boot the
      // app into an authenticated shell with nothing behind it, then bounce to
      // /login on the first 401.
      storage: createJSONStorage(() => authStateStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
) 